import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Empty,
  List,
  Row,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowRightOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  PauseCircleOutlined,
  QrcodeOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { chatAPI, doctorLinkAPI, doctorsAPI } from '../utils/api';

const { Title, Text } = Typography;

interface DashboardPatient {
  patient_id: number;
  full_name: string;
  week_of_pregnancy?: number;
  due_date?: string;
  access_level?: string;
  is_data_hidden_temporarily?: boolean;
  last_activity_at?: string | null;
  latest_metrics?: Record<string, any>;
  chat_conversation_id?: number | null;
  last_message_preview?: string | null;
  unread_messages?: number;
}

interface IncomingDoctorLinkRow {
  link: {
    id: string;
    patient_id: number;
    access_level: string;
    status: string;
  };
  patient_full_name: string;
}

interface DashboardResponse {
  active_patients_count: number;
  pending_requests_count: number;
  paused_sharing_count: number;
  due_soon_count: number;
  patients: DashboardPatient[];
}

const ACCESS_LABELS: Record<string, string> = {
  ultrasound_only: 'Только УЗИ',
  ultrasound_labs: 'УЗИ и анализы',
  full: 'Весь дневник',
};

export const DoctorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [incoming, setIncoming] = useState<IncomingDoctorLinkRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [incomingLoading, setIncomingLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await doctorsAPI.getDashboard();
      setDashboard((response.data || null) as DashboardResponse | null);
    } catch (error: any) {
      console.error('Error loading dashboard:', error);
      message.error(error.response?.data?.detail || 'Не удалось загрузить dashboard врача');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadIncoming = useCallback(async () => {
    try {
      setIncomingLoading(true);
      const response = await doctorLinkAPI.doctorIncomingLinks();
      setIncoming((response.data as IncomingDoctorLinkRow[]) ?? []);
    } catch (error: any) {
      console.error('Error loading incoming links:', error);
      message.error(error.response?.data?.detail || 'Не удалось загрузить входящие заявки');
    } finally {
      setIncomingLoading(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    await Promise.all([loadDashboard(), loadIncoming()]);
  }, [loadDashboard, loadIncoming]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const handleAcceptIncoming = async (linkId: string) => {
    try {
      await doctorLinkAPI.doctorAcceptLink(linkId);
      message.success('Привязка подтверждена');
      await loadAll();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Ошибка подтверждения');
    }
  };

  const handleRejectIncoming = async (linkId: string) => {
    try {
      await doctorLinkAPI.doctorRejectLink(linkId);
      message.success('Заявка отклонена');
      await loadAll();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Ошибка отклонения');
    }
  };

  const patients = dashboard?.patients || [];
  const dueSoonPatientsHref = '/doctors?due_soon=1&sort_by=due_date&order=asc';
  const pausedPatientsHref = '/doctors?paused_only=1';

  const scrollToIncomingRequests = useCallback(() => {
    const el = document.getElementById('incoming-requests');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useEffect(() => {
    if (window.location.hash === '#incoming-requests') {
      scrollToIncomingRequests();
    }
  }, [scrollToIncomingRequests, incomingLoading, incoming.length]);

  const getPatientDisplayName = (patient: DashboardPatient) => {
    const name = patient.full_name?.trim();
    if (!name || name.startsWith('gAAAA')) {
      return `Пациентка #${patient.patient_id}`;
    }
    return name;
  };

  const openPatientChat = async (patient: DashboardPatient) => {
    if (patient.chat_conversation_id) {
      navigate(`/chat/${patient.chat_conversation_id}`);
      return;
    }
    try {
      const response = await chatAPI.ensureConversationWithPatient(patient.patient_id);
      const conversationId = (response.data as any)?.id;
      if (conversationId) {
        navigate(`/chat/${conversationId}`);
        return;
      }
      message.info('Не удалось открыть чат');
      navigate('/chats');
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Не удалось открыть чат');
    }
  };

  const highlights = useMemo(
    () => [
      {
        title: 'Активные пациентки',
        value: dashboard?.active_patients_count || 0,
        icon: <UserOutlined />,
        onClick: () => navigate('/doctors'),
      },
      {
        title: 'Ожидают решения',
        value: dashboard?.pending_requests_count || 0,
        icon: <ClockCircleOutlined />,
        onClick: () => {
          if (window.location.hash !== '#incoming-requests') {
            window.history.replaceState(null, '', '#incoming-requests');
          }
          scrollToIncomingRequests();
        },
      },
      {
        title: 'Данные временно скрыты',
        value: dashboard?.paused_sharing_count || 0,
        icon: <PauseCircleOutlined />,
        onClick: () => navigate(pausedPatientsHref),
      },
      {
        title: 'ПДР скоро',
        value: dashboard?.due_soon_count || 0,
        icon: <CalendarOutlined />,
        onClick: () => navigate(dueSoonPatientsHref),
      },
    ],
    [dashboard, navigate, scrollToIncomingRequests]
  );

  const renderMetric = (patient: DashboardPatient) => {
    const latest = patient.latest_metrics || {};
    if (latest.glucose?.glucose_level) {
      return <Text type="secondary">Глюкоза {latest.glucose.glucose_level} ммоль/л</Text>;
    }
    if (latest.weight?.weight_kg) {
      return <Text type="secondary">Вес {latest.weight.weight_kg} кг</Text>;
    }
    if (latest.lab_test?.test_type) {
      return <Text type="secondary">Последний анализ: {latest.lab_test.test_type}</Text>;
    }
    return <Text type="secondary">Пока нет недавних метрик</Text>;
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: '#263238', marginBottom: 8 }}>Рабочее место врача</Title>
        <Text type="secondary">
          Дашборд наблюдения, новые заявки, быстрый переход к пациенткам, чатам и QR врача.
        </Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {highlights.map((item) => (
          <Col xs={24} sm={12} xl={6} key={item.title}>
            <Card bordered={false} loading={loading} style={{ borderRadius: 18 }}>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Text type="secondary">{item.title}</Text>
                <Space align="center">
                  <span style={{ color: '#7CB342', fontSize: 20 }}>{item.icon}</span>
                  {item.onClick ? (
                    <Button type="link" onClick={item.onClick} style={{ padding: 0, fontSize: 28, fontWeight: 700, height: 'auto' }}>
                      {item.value}
                    </Button>
                  ) : (
                    <Text strong style={{ fontSize: 28 }}>
                      {item.value}
                    </Text>
                  )}
                </Space>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} xl={16}>
          <Card
            title="Активные пациентки"
            extra={<Button type="link" onClick={() => navigate('/doctors')}>Открыть весь список</Button>}
            loading={loading}
            style={{ borderRadius: 18 }}
          >
            {patients.length === 0 && !loading ? (
              <Empty description="Активных пациенток пока нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                dataSource={patients.slice(0, 6)}
                renderItem={(patient) => (
                  <List.Item
                    actions={[
                      <Button key="open" type="link" onClick={() => navigate(`/doctors/patient/${patient.patient_id}`)}>
                        Карточка
                      </Button>,
                      <Button
                        key="chat"
                        type="link"
                        icon={<MessageOutlined />}
                        onClick={() => void openPatientChat(patient)}
                      >
                        Чат
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<UserOutlined style={{ fontSize: 20, color: '#7CB342' }} />}
                      title={
                        <Space wrap>
                          <Text strong>{getPatientDisplayName(patient)}</Text>
                          {patient.week_of_pregnancy ? <Tag color="blue">{patient.week_of_pregnancy} нед.</Tag> : null}
                          {patient.access_level ? <Tag>{ACCESS_LABELS[patient.access_level] || patient.access_level}</Tag> : null}
                          {patient.is_data_hidden_temporarily ? <Tag color="orange">Данные скрыты пациенткой</Tag> : null}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={4}>
                          <Text type="secondary">
                            {patient.due_date ? `ПДР ${dayjs(patient.due_date).format('DD.MM.YYYY')}` : 'ПДР не указана'}
                            {patient.last_activity_at ? ` · активность ${dayjs(patient.last_activity_at).format('DD.MM HH:mm')}` : ''}
                          </Text>
                          {renderMetric(patient)}
                          {patient.last_message_preview ? (
                            <Text type="secondary">Чат: {patient.last_message_preview}</Text>
                          ) : null}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} xl={8}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card
              id="incoming-requests"
              title={`Новые заявки${incoming.length ? ` (${incoming.length})` : ''}`}
              extra={<Button type="link" onClick={loadIncoming}>Обновить</Button>}
              loading={incomingLoading}
              style={{ borderRadius: 18 }}
            >
              {incoming.length === 0 && !incomingLoading ? (
                <Empty description="Новых запросов нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <List
                  dataSource={incoming.slice(0, 5)}
                  renderItem={(row) => (
                    <List.Item
                      actions={[
                        <Button type="primary" size="small" onClick={() => handleAcceptIncoming(row.link.id)}>
                          Принять
                        </Button>,
                        <Button danger size="small" onClick={() => handleRejectIncoming(row.link.id)}>
                          Отклонить
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        title={<Text strong>{row.patient_full_name || 'Без имени'}</Text>}
                        description={<Tag color="processing">{ACCESS_LABELS[row.link.access_level] || row.link.access_level}</Tag>}
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>

            <Card title="Быстрые переходы" style={{ borderRadius: 18 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button block icon={<ArrowRightOutlined />} onClick={() => navigate('/doctors')}>
                  Все пациентки
                </Button>
                <Button block icon={<MessageOutlined />} onClick={() => navigate('/chats')}>
                  Чаты
                </Button>
                <Button block icon={<QrcodeOutlined />} onClick={() => navigate('/profile')}>
                  QR и публичный ID
                </Button>
              </Space>
            </Card>
          </Space>
        </Col>
      </Row>
    </div>
  );
};
